import { useState, useRef } from "react";
import { ImagePlus, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/lib/i18n";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

export const PostComposer = ({ onPosted }) => {
  const { user } = useAuth();
  const { t } = useLang();
  const fileRef = useRef(null);
  const [content, setContent] = useState("");
  const [image, setImage] = useState("");
  const [uploading, setUploading] = useState(false);
  const [posting, setPosting] = useState(false);

  if (!user || !user.association_slug || !["association_admin", "mosque_admin", "moderator"].includes(user.role)) return null;

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/associations/${user.association_slug}/upload`, fd);
      setImage(data.url);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const publish = async () => {
    setPosting(true);
    try {
      await api.post(`/associations/${user.association_slug}/posts`, { content, image });
      setContent("");
      setImage("");
      toast.success(t("post_published"));
      onPosted?.();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="border border-border rounded-xl bg-card p-4 mb-6" data-testid="post-composer">
      <div className="flex gap-3">
        <Avatar className="h-9 w-9 border border-border shrink-0">
          <AvatarImage src={user.picture} alt={user.name} />
          <AvatarFallback className="text-xs font-bold">{user.name?.slice(0, 2).toUpperCase()}</AvatarFallback>
        </Avatar>
        <Textarea
          data-testid="post-composer-input"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder={t("post_placeholder")}
          className="min-h-[60px] resize-none border-0 bg-transparent focus-visible:ring-0 px-0 text-sm shadow-none"
        />
      </div>
      {image && (
        <div className="relative mt-3">
          <img src={image} alt="" className="w-full max-h-64 object-cover rounded-lg" />
          <button data-testid="post-composer-remove-image" onClick={() => setImage("")} className="absolute top-2 right-2 h-7 w-7 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80 transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="post-composer-file-input" />
        <Button type="button" variant="ghost" size="sm" data-testid="post-composer-image-btn" onClick={() => fileRef.current?.click()} disabled={uploading} className="text-muted-foreground rounded-full">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
        </Button>
        <Button data-testid="post-composer-publish-btn" size="sm" onClick={publish} disabled={posting || (!content.trim() && !image)} className="rounded-full active:scale-95">
          {posting ? "..." : t("publish")}
        </Button>
      </div>
    </div>
  );
};
